import { type ReactElement, useRef } from 'react';

import {
  Chip,
  EncoreProvider,
  Focusable,
  IconChevronDown,
} from '@spotify-internal/encore-web';
import { Tooltip, TooltipRoot } from '@spotify-internal/encore-web/next';

import { FilterEditorPopover } from './FilterEditorPopover';
import { HostedFilterEditorContext } from './HostedFilterEditorContext';
import { isEmptyFilterValue } from './isEmptyFilterValue';
import {
  type FilterDefinition,
  useFilters,
  useOptionalFilterEditor,
} from './provider';

import styles from './InlineFilterTrigger.module.css';

export interface InlineFilterTriggerProps {
  filter: FilterDefinition;
}

export const InlineFilterTrigger = ({
  filter,
}: InlineFilterTriggerProps): ReactElement => {
  const { activeFilters, addFilter, removeFilter } = useFilters();
  const editorContext = useOptionalFilterEditor();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const editedValueRef = useRef<{ value: unknown } | null>(null);
  const open = editorContext?.activeEditorFilterId === filter.id;
  const active = activeFilters.find(f => f.id === filter.id);
  const isToggle = filter.interaction === 'toggle';

  const restoreFocus = (): void => {
    queueMicrotask(() => {
      if (document.activeElement === document.body) {
        anchorRef.current?.querySelector('button')?.focus();
      }
    });
  };

  const removeInlineFilter = (): void => {
    removeFilter(filter.id);
    restoreFocus();
  };

  const handleValueChange = (value: unknown): void => {
    editedValueRef.current = { value };
    addFilter(filter, value);
  };

  const handleOpenChange = (nextOpen: boolean): void => {
    const latestValue = editedValueRef.current
      ? editedValueRef.current.value
      : active?.value;
    editedValueRef.current = null;
    if (!nextOpen && active && isEmptyFilterValue(latestValue)) {
      removeInlineFilter();
    }
    editorContext?.setActiveEditorFilterId(nextOpen ? filter.id : null);
    if (!nextOpen) restoreFocus();
  };

  if (isToggle) {
    return (
      <span ref={anchorRef} className={styles.anchor}>
        {(active ?? filter.activate(false)).renderViewer({
          onValueChange: handleValueChange,
          onRemove: active
            ? removeInlineFilter
            : () => {
                handleValueChange(true);
                restoreFocus();
              },
        })}
      </span>
    );
  }

  return (
    <span ref={anchorRef} className={styles.anchor}>
      <FilterEditorPopover
        label={filter.label}
        isOpen={open}
        onOpenChange={handleOpenChange}
        trigger={triggerRef => {
          if (active && !isEmptyFilterValue(active.value)) {
            return (
              <HostedFilterEditorContext.Provider
                value={{
                  filterId: filter.id,
                  isOpen: open,
                  onOpenChange: handleOpenChange,
                  triggerRef,
                }}
              >
                {active.renderViewer({
                  onValueChange: handleValueChange,
                  onRemove: removeInlineFilter,
                })}
              </HostedFilterEditorContext.Provider>
            );
          }
          const chip = (
            <Chip
              ref={triggerRef}
              size="small"
              variant="tinted"
              iconTrailing={IconChevronDown}
              type="button"
              aria-expanded={open}
              aria-haspopup="dialog"
              onClick={() => handleOpenChange(!open)}
             data-track-source-anchor="5c31bbb977;inline-filter-trigger-chip">
              {filter.label}
            </Chip>
          );
          return (
            <EncoreProvider config={{ next: ['chip'] }}>
              {filter.description ? (
                <TooltipRoot>
                  <Focusable>{chip}</Focusable>
                  <Tooltip>{filter.description}</Tooltip>
                </TooltipRoot>
              ) : (
                chip
              )}
            </EncoreProvider>
          );
        }}
      >
        {filter.renderEditor({
          value: active?.value ?? null,
          onValueChange: handleValueChange,
          onClose: () => handleOpenChange(false),
        })}
      </FilterEditorPopover>
    </span>
  );
};
